import paper from 'paper'
import { IPoint, ISize } from '@consts/types';



type VerticalDirections = 'top' | 'down';
type HorizontalDirections = 'left' | 'right';
type SimpleDirections = VerticalDirections | HorizontalDirections;

/* 
*   The direction in diagonal type names equals direction
*   from bottom to top for diagonal.
*
*   More about types values: 
*   diagonal-lb-rt - ↗
*   diagonal-rt-lb - ↙
*   diagonal-rb-lt - ↖
*   diagonal-lt-rb - ↘
**/
type DiagonalRightDirections = 'diagonal-lb-rt' | 'diagonal-rt-lb';
type DiagonalLeftDirections = 'diagonal-rb-lt' | 'diagonal-lt-rb';
type DiagonalDirections = DiagonalRightDirections | DiagonalLeftDirections;

type Directions = SimpleDirections | DiagonalDirections;

type ResizeCursor = 'ew-resize' | 'ns-resize' | 'nesw-resize' | 'nwse-resize';

type CursorDirectionMap = { [key in Directions]: ResizeCursor };
type MapBySimpleDirections<T extends string> = {
  [horizontalKey in HorizontalDirections]: {
    [verticalKey in VerticalDirections]: T;
  }
}

type ResizeHandler = (size: ISize, position: IPoint) => void;
type MouseEventHandler = (event: MouseEvent) => void;
type TouchEventHandler = (event: TouchEvent) => void;

const changingXDirections: Directions[] = [
  'left',
  'diagonal-rt-lb',
  'diagonal-rb-lt'
]

const changingYDirections: Directions[] = [
  'top',
  'diagonal-lb-rt',
  'diagonal-rb-lt'
]

const horizontalResizeDirection: string[] = [
  'left',
  'right',
  'diagonal-lt-rb',
  'diagonal-rb-lt',
  'diagonal-lb-rt',
  'diagonal-rt-lb'
];
const verticalResizeDirection: string[] = [
  'down',
  'top',
  'diagonal-lt-rb',
  'diagonal-rb-lt',
  'diagonal-lb-rt',
  'diagonal-rt-lb'
];

const cursorDirectionMap: CursorDirectionMap = {
  "down": 'ns-resize',
  "top": 'ns-resize',
  "left": 'ew-resize',
  "right": 'ew-resize',
  "diagonal-lb-rt": 'nesw-resize',
  "diagonal-rb-lt": 'nwse-resize',
  "diagonal-lt-rb": 'nwse-resize',
  "diagonal-rt-lb": 'nesw-resize'
}

const diagonalDirMap: MapBySimpleDirections<DiagonalDirections> = {
  right: {
    down: 'diagonal-lt-rb',
    top: 'diagonal-lb-rt'
  },
  left: {
    down: 'diagonal-rt-lb',
    top: 'diagonal-rb-lt'
  }
}

//  FIXME: Has problem where STROKE_WIDTH equals 10
//  On right border created wrong cursor icon in case
//  where cursor hover on max left postilion
const STROKE_VISIBLE_WIDTH = 2;
const STROKE_WIDTH = 20;

const DEFAULT_EXPAND_SIZE = 15;

export default class ResizeArea {
  public onResize: ResizeHandler | null = null;

  private _resizeHoverRectangle: paper.Path.Rectangle;
  private _resizeVisibleRectangle: paper.Path.Rectangle;

  private _cursorBeforeModify: string | null = null;
  private _isShow: boolean = false;

  private _onDocumentMouseUp: VoidFunction | null = null;
  private _onDocumentMouseMove: MouseEventHandler  | null = null;
  private _onDocumentTouchMove: TouchEventHandler  | null = null;


  constructor(
    private paperScope: paper.PaperScope,
    rectangle: paper.Rectangle,
    private expandSize: number = DEFAULT_EXPAND_SIZE,
  ) {
    const resizeVisibleRectangle = this.self.expandRectangle(rectangle, this.expandSize);
    const rectangleVisibleColor = new this.paperScope.Color(0, 0, 0, 0.2);
    this._resizeVisibleRectangle = new this.paperScope.Path.Rectangle(resizeVisibleRectangle)

    this._resizeVisibleRectangle.strokeColor = rectangleVisibleColor;
    this._resizeVisibleRectangle.strokeWidth = STROKE_VISIBLE_WIDTH;
    this._resizeVisibleRectangle.visible = false;

    const resizeHoverRectangle = this.self.expandRectangle(rectangle, this.expandSize);
    const rectangleHoverColor = new this.paperScope.Color(0, 0, 0, Number.EPSILON);
    this._resizeHoverRectangle = new this.paperScope.Path.Rectangle(resizeHoverRectangle)

    this._resizeHoverRectangle.strokeColor = rectangleHoverColor;
    this._resizeHoverRectangle.strokeWidth = STROKE_WIDTH;
    this._resizeHoverRectangle.visible = false;

    this.bindMouseHoverHandlers();

    this._resizeHoverRectangle.onMouseDown  = (event: paper.MouseEvent) => {
      this.handleMouseDown(event)
    }
  }

  public updateSize(rectangle: paper.Rectangle) {
    const updateBounds = this.self.expandRectangle(rectangle, this.expandSize);
    this._resizeVisibleRectangle.bounds = updateBounds.clone();
    this._resizeHoverRectangle.bounds = updateBounds;
  }

  public get isShow() {
    return this._isShow;
  }
  public set isShow(state: boolean) {
    if (this._isShow === state) return;
    this._resizeVisibleRectangle.visible = state;
    this._resizeHoverRectangle.visible = state;
    this._isShow = state;
  }

  public shift(x: number, y: number) {
    this._resizeHoverRectangle.position.x += x;
    this._resizeHoverRectangle.position.y += y;
  
    this._resizeVisibleRectangle.position.x += x;
    this._resizeVisibleRectangle.position.y += y;
  }

  public bringToFront() {
    this._resizeVisibleRectangle.bringToFront();
    this._resizeHoverRectangle.bringToFront();
  }

  public remove() {
    this._resizeVisibleRectangle.remove();
    this._resizeHoverRectangle.remove();
  }

  protected unbindMouseHoverHandlers = () => {
    this._resizeHoverRectangle.onMouseMove = null;
    this._resizeHoverRectangle.onMouseLeave = null;
  }

  protected bindMouseHoverHandlers = () => {
    this._resizeHoverRectangle.onMouseMove = (event: paper.MouseEvent) => {
      this.handleMouseMove(event)
    }
    this._resizeHoverRectangle.onMouseLeave = () => {
      this.handleBlur()
    }
  }

  protected handleMouseDown = (event: paper.MouseEvent) => {
    this.unbindMouseHoverHandlers();

    const mouseUpHandler = () => this.handleMouseUp();
    window.addEventListener('mouseup', mouseUpHandler);
    window.addEventListener('touchend', mouseUpHandler);
    this._onDocumentMouseUp = mouseUpHandler;

    
    const mouseMoveHandler = this.resizeHandlerCreator(event);
    const touchMoveHandler = this.resizeTouchHandlerCreator(event);
    window.addEventListener('mousemove', mouseMoveHandler);
    window.addEventListener('touchmove', touchMoveHandler);
    this._onDocumentMouseMove = mouseMoveHandler;
    this._onDocumentTouchMove = touchMoveHandler;
  }

  protected handleMouseUp = () => {
    if (this._onDocumentMouseUp) {
      window.removeEventListener('mouseup', this._onDocumentMouseUp);
      window.removeEventListener('touchend', this._onDocumentMouseUp);
    }
    if (this._onDocumentMouseMove) {
      window.removeEventListener('mousemove', this._onDocumentMouseMove);
      // @ts-ignore
      window.removeEventListener('touchmove', this._onDocumentTouchMove as unknown as (this:Window, ev: MouseEvent) => any);
    }

    this.bindMouseHoverHandlers();
  }

  protected handleMouseMove = (event: paper.MouseEvent) => {
    const direction = this.calculateDirection(event);
    const cursor = cursorDirectionMap[direction];

    if (this._cursorBeforeModify === null) {
      this._cursorBeforeModify = document.body.style.cursor;
    }

    document.body.style.cursor = cursor;
  }

  protected handleBlur = () => {
    if (this._cursorBeforeModify != null) {
      document.body.style.cursor = this._cursorBeforeModify;
      this._cursorBeforeModify = null;
    }
  }

  protected resizeHandlerCreator(event: paper.MouseEvent): MouseEventHandler {
    const direction = this.calculateDirection(event);
    const isXPositionChange = changingXDirections.includes(direction);
    const isYPositionChange = changingYDirections.includes(direction);
    const initBounds = this._resizeHoverRectangle.bounds.clone();
    const { point: initResizePoint } = event;

    return (event: MouseEvent) => {
      const { x: initX, y: initY} = initResizePoint;
      const { offsetX: x, offsetY: y } = event;

      let diffX = horizontalResizeDirection.includes(direction) ? x - initX : 0;
      let diffY = verticalResizeDirection.includes(direction) ? y - initY : 0;

      diffX = isXPositionChange ? -diffX : diffX;
      diffY = isYPositionChange ? -diffY : diffY;
      

      const bounds = initBounds.clone();
      const updateBoundsWidth = bounds.width + diffX;
      const updateBoundsHeight = bounds.height + diffY;

      if (updateBoundsWidth > 10) {
        bounds.width = updateBoundsWidth;
        bounds.x += isXPositionChange ? -diffX : 0;
      } else {
        bounds.width = this._resizeHoverRectangle.bounds.width;
        bounds.x = this._resizeHoverRectangle.bounds.x;
      }

      if (updateBoundsHeight > 10) {
        bounds.height = updateBoundsHeight;
        bounds.y += isYPositionChange ? -diffY : 0;
      } else {
        bounds.height = this._resizeHoverRectangle.bounds.height;
        bounds.y = this._resizeHoverRectangle.bounds.y;
      }
      
      this._resizeVisibleRectangle.bounds = bounds.clone();
      this._resizeHoverRectangle.bounds = bounds;
      if (this.onResize) {
        const originalRectangle = this.self.unExpandRectangle(bounds, this.expandSize);
        const size: ISize = { width: originalRectangle.width, height: originalRectangle.height };
        const position: IPoint = { x: originalRectangle.x, y: originalRectangle.y };
        this.onResize(size, position);
      }
    }
  }

  protected resizeTouchHandlerCreator(event: paper.MouseEvent): TouchEventHandler {
    const direction = this.calculateDirection(event);

    const isXPositionChange = changingXDirections.includes(direction);
    const isYPositionChange = changingYDirections.includes(direction);
    const initBounds = this._resizeHoverRectangle.bounds.clone();
    const { point: initResizePoint } = event;

    return (event: TouchEvent) => {
      if (!event.touches[0]) return;
      const { x: initX, y: initY} = initResizePoint;
      const { clientX: x, clientY: y } = event.touches[0];

      let diffX = horizontalResizeDirection.includes(direction) ? x - initX : 0;
      let diffY = verticalResizeDirection.includes(direction) ? y - initY : 0;

      diffX = isXPositionChange ? -diffX : diffX;
      diffY = isYPositionChange ? -diffY : diffY;


      const bounds = initBounds.clone();
      const updateBoundsWidth = bounds.width + diffX;
      const updateBoundsHeight = bounds.height + diffY;

      if (updateBoundsWidth > 10) {
        bounds.width = updateBoundsWidth;
        bounds.x += isXPositionChange ? -diffX : 0;
      } else {
        bounds.width = this._resizeHoverRectangle.bounds.width;
        bounds.x = this._resizeHoverRectangle.bounds.x;
      }

      if (updateBoundsHeight > 10) {
        bounds.height = updateBoundsHeight;
        bounds.y += isYPositionChange ? -diffY : 0;
      } else {
        bounds.height = this._resizeHoverRectangle.bounds.height;
        bounds.y = this._resizeHoverRectangle.bounds.y;
      }

      this._resizeVisibleRectangle.bounds = bounds.clone();
      this._resizeHoverRectangle.bounds = bounds;
      if (this.onResize) {
        const originalRectangle = this.self.unExpandRectangle(bounds, this.expandSize);
        const size: ISize = { width: originalRectangle.width, height: originalRectangle.height };
        const position: IPoint = { x: originalRectangle.x, y: originalRectangle.y };
        this.onResize(size, position);
      }
    }
  }

  protected calculateDirection = (event: paper.MouseEvent): Directions => {
    const { height, width, x, y } = this._resizeHoverRectangle.bounds;
    const onePercentForWidth = (width + STROKE_WIDTH/2)/100;
    const onePercentForHeight = (height + STROKE_WIDTH/2)/100;

    const strokeHorizontalPercent = STROKE_WIDTH / onePercentForWidth;
    const strokeVerticalPercent = STROKE_WIDTH / onePercentForHeight;

    const horizontalTriggerValue = (100 - (strokeHorizontalPercent * 2))/2;
    const verticalTriggerValue = (100 - (strokeHorizontalPercent * 2))/2;

    const percentByX = (event.point.x - x) / onePercentForWidth;
    const percentByY = (event.point.y - y) / onePercentForHeight;

    const diffX = percentByX - 50;
    const diffY = percentByY - 50;

    const isPointOnDiagonal = 
      Math.abs(diffX) >= horizontalTriggerValue 
      && Math.abs(diffY) >= verticalTriggerValue;


    if (isPointOnDiagonal) {
      const HorizontalDir: HorizontalDirections = diffX > 0 ? 'right' : 'left';
      const verticalDir: VerticalDirections = diffY > 0 ? 'down' : 'top';
      return diagonalDirMap[HorizontalDir][verticalDir];
    } else {
      // TODO: make a complete algorithm
      if (percentByX < strokeVerticalPercent) return 'left';
      if (percentByX > 100 - strokeVerticalPercent) return 'right';
      if (percentByY < strokeHorizontalPercent) return 'top';
      if (percentByY > 100 - strokeHorizontalPercent) return 'down';
      return 'down';
    }
  }

  protected get self() {
    return ResizeArea;
  }

  static expandRectangle(
    rectangle: paper.Rectangle,
    expandSize: number
  ): paper.Rectangle {
    const rectangleClone = rectangle.clone();
    rectangleClone.x -= expandSize;
    rectangleClone.y -= expandSize;
    rectangleClone.width += expandSize * 2;
    rectangleClone.height += expandSize * 2;
    return rectangleClone;
  }

  static unExpandRectangle(
    rectangle: paper.Rectangle,
    expandSize: number
  ): paper.Rectangle {
    const rectangleClone = rectangle.clone();
    rectangleClone.x += expandSize;
    rectangleClone.y += expandSize;
    rectangleClone.width -= expandSize * 2;
    rectangleClone.height -= expandSize * 2;
    return rectangleClone;
  }
};